import { Temporal } from 'temporal-polyfill';

const defaultLocale = 'en-US';

export class DateService {
  static getFormat = (userTimeZone: string): Intl.DateTimeFormatOptions => {
    const formatOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: userTimeZone,
    };
    return formatOptions;
  };

  static getUtcNow = () => Temporal.Now.instant();

  static localTimeFromUtc = (utcString: string) => {
    if (!utcString) {
      return '';
    }
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const utcInstant = Temporal.Instant.from(utcString);
    const localTime = utcInstant.toZonedDateTimeISO(userTimeZone);

    return localTime.toString();
  };

  static utcFromLocalTime = (localTimeString: string, timeZone?: string) => {
    if (!localTimeString) {
      return '';
    }

    const userTimeZone = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const plainDateTime = Temporal.Instant.from(localTimeString);
    const zoned = plainDateTime.toZonedDateTimeISO(userTimeZone);
    const utcInstant = zoned.toInstant();
    return utcInstant.toString();
  };

  // Normalizes loose date-time strings (e.g. '2021-09-01 12:13:44') into a valid UTC instant string.
  static toInstantString = (value: string) => {
    const normalized = value.replace(' ', 'T');
    return /([zZ]|[+-]\d{2}:?\d{2})$/.test(normalized) ? normalized : `${normalized}Z`;
  };

  static format = (utcString: string, formatOptions?: Intl.DateTimeFormatOptions) => {
    if (!utcString) {
      return '';
    }
    const userLocale = navigator.language || defaultLocale;
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const utcInstant = Temporal.Instant.from(DateService.toInstantString(utcString));
    const localTime = utcInstant.toZonedDateTimeISO(userTimeZone);
    const plainDate = new Date(localTime.epochMilliseconds);
    const options = formatOptions || DateService.getFormat(userTimeZone);

    return new Intl.DateTimeFormat(userLocale, options).format(plainDate);
  };

  static formatTime = (utcString: string) => DateService.format(utcString, { hour: 'numeric', minute: 'numeric', hour12: false });

  static formatDate = (utcString: string) => DateService.format(utcString, { year: 'numeric', month: 'long', day: 'numeric' });

  static isToday = (utcString: string) => DateService.formatDate(utcString) === DateService.formatDate(new Date().toISOString());

  static isYesterday = (utcString: string) => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return DateService.formatDate(utcString) === DateService.formatDate(yesterday.toISOString());
  };

  static isExcelFormat = (date: string) => {
    return !/^\d{4}-\d{2}-\d{2}(T.*Z)?$/.test(date);
  };

  // '1/8/1999'have no idea what will be in excel
  static excelFormatToData = (excelString: string) => {
    if (!excelString) {
      return '';
    }
    const date = new Date(excelString);
    const isoDate = date.toISOString();
    return isoDate;
  };

  static utcFromLocalTimeByKeys = (object: any, keys: string[]) => {
    let copy;
    if (Array.isArray(object)) {
      copy = [...object];
      copy.forEach(item => {
        for (const key of keys) {
          if (item[key]) {
            item[key] = DateService.isExcelFormat(item[key])
              ? DateService.utcFromLocalTime(DateService.excelFormatToData((item[key])))
              : DateService.utcFromLocalTime(item[key]);
          }
        }
      });
    } else {
      copy = { ...object };
      for (const key of keys) {
        if (copy[key]) {
          copy[key] = DateService.isExcelFormat(copy[key])
            ? DateService.utcFromLocalTime(DateService.excelFormatToData((copy[key])))
            : DateService.utcFromLocalTime(copy[key]);
        }
      }
    }

    return copy;
  };
}
